import React from "react";
import { COMPONENT_OUTLINE_BORDER } from "../theme/componentBorders";

// 统计卡片配置接口
export interface StatisticCardConfig {
  key: string; // 唯一标识
  title: string; // 标题
  value: number | string; // 数值
  iconKey?: string; // 图标标识
  prefix?: React.ReactNode; // 前缀图标（直接传入的图标组件）
  suffix?: string; // 后缀单位
  valueStyle?: React.CSSProperties; // 数值样式
  precision?: number; // 小数位数
  formatter?: (value: number | string, config?: any) => React.ReactNode; // 自定义格式化函数
  cardProps?: React.HTMLAttributes<HTMLDivElement>; // 卡片属性
  statisticProps?: React.HTMLAttributes<HTMLDivElement>; // 统计组件属性
  clickable?: boolean; // 是否可点击，默认为false
  onClick?: (item: StatisticCardConfig, event: React.MouseEvent<HTMLDivElement>) => void; // 点击事件回调
}

// 组件属性接口
export interface GenericCardsStatisticProps {
  data: StatisticCardConfig[]; // 统计数据
  columns?: number; // 每行显示的列数，默认为4
  gutter?: [number, number]; // 卡片间距，默认为[16, 16]
  cardStyle?: React.CSSProperties; // 卡片样式
  showCards?: boolean; // 是否显示卡片包装，默认为true
  className?: string; // 自定义类名
  style?: React.CSSProperties; // 自定义样式
}

/**
 * 通用统计卡片组件
 * 根据传入的数据动态渲染不同数量的统计卡片
 * 不依赖 antd，使用原生 HTML 和 CSS
 */
const GenericCardsStatistic: React.FC<GenericCardsStatisticProps> = ({
  data = [],
  columns = 4,
  gutter = [16, 0],
  cardStyle = {},
  showCards = true,
  className = "",
  style = {},
}) => {
  // 计算每个卡片占用的列数
  const getColSpan = () => {
    return 24 / columns;
  };

  // 渲染单个统计卡片
  const renderStatisticCard = (item: StatisticCardConfig) => {
    // 判断是否可点击
    const isClickable = item.clickable === true || !!item.onClick;
    
    // 处理点击事件
    const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
      console.log(`[GenericCardsStatistic] Click handler called for card ${item.key}`, {
        isClickable,
        hasOnClick: !!item.onClick,
        event,
      });
      if (isClickable && item.onClick) {
        console.log(`[GenericCardsStatistic] Calling item.onClick for card ${item.key}`);
        item.onClick(item, event);
      } else {
        console.log(`[GenericCardsStatistic] onClick not called for card ${item.key}`, {
          isClickable,
          hasOnClick: !!item.onClick,
        });
      }
    };
    
    // 处理键盘事件（用于可访问性）
    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (isClickable && item.onClick && (event.key === "Enter" || event.key === " ")) {
        event.preventDefault();
        item.onClick(item, event as any);
      }
    };
    
    // 处理鼠标移入事件
    const handleMouseEnter = (event: React.MouseEvent<HTMLDivElement>) => {
      // 可以在这里添加鼠标移入的逻辑
    };
    
    // 统一缩小字号：标题更小、数值略小；允许调用方通过 valueStyle 覆盖
    const mergedValueStyle: React.CSSProperties = {
      fontSize: 22,
      fontWeight: 600,
      color: "#262626",
      ...(item.valueStyle || {}),
    };

    // 处理图标
    const prefixIcon = item.prefix;

    // 格式化数值
    const formatValue = (value: number | string) => {
      if (typeof value === "number") {
        if (item.precision !== undefined) {
          return value.toFixed(item.precision);
        }
        return value.toString();
      }
      return value;
    };

    const statisticContent = (
      <div
        className="statistic-content"
        style={{
          textAlign: "center",
          padding: "16px",
          ...item.statisticProps?.style,
        }}
        {...item.statisticProps}
      >
        <div
          className="statistic-title"
          style={{
            fontSize: 12,
            color: "#8c8c8c",
            marginBottom: "8px",
          }}
        >
          {item.title}
        </div>
        <div className="statistic-value" style={mergedValueStyle}>
          {prefixIcon && (
            <span style={{ marginRight: "8px", fontSize: "20px" }}>
              {prefixIcon}
            </span>
          )}
          {item.formatter
            ? item.formatter(item.value, item)
            : formatValue(item.value)}
          {item.suffix && (
            <span style={{ fontSize: "14px", marginLeft: "4px" }}>
              {item.suffix}
            </span>
          )}
        </div>
      </div>
    );

    // 如果显示卡片包装，则用Card包装
    if (showCards) {
      // 先提取样式，避免 cursor 被覆盖
      const { style: cardPropsStyle, ...restCardProps } = item.cardProps || {};
      
      // 合并样式，确保 cursor 在最后，不会被其他样式覆盖
      const mergedCardStyle: React.CSSProperties = {
        background: "#fff",
        borderRadius: "6px",
        boxShadow:
          "0 1px 2px 0 rgba(0, 0, 0, 0.02), 0 1px 6px -1px rgba(0, 0, 0, 0.01), 0 2px 4px 0 rgba(0, 0, 0, 0.01)",
        border: COMPONENT_OUTLINE_BORDER,
        ...cardStyle,
        ...cardPropsStyle,
        // cursor 放在最后，如果可点击则强制设置为 pointer，确保不会被其他样式覆盖
        cursor: isClickable ? "pointer" : undefined,
      };
      
      // 提取其他 cardProps，避免覆盖我们的属性
      const { 
        onClick: cardPropsOnClick, 
        onKeyDown: cardPropsOnKeyDown,
        onMouseEnter: cardPropsOnMouseEnter,
        ...otherCardProps 
      } = restCardProps;
      
      // 合并事件处理器
      const handleCardClick = (event: React.MouseEvent<HTMLDivElement>) => {
        console.log(`[GenericCardsStatistic] Card clicked: ${item.key}`, {
          isClickable,
          hasOnClick: !!item.onClick,
          hasCardPropsOnClick: !!cardPropsOnClick,
          event,
        });
        // 先执行我们的 handleClick（如果可点击）
        if (isClickable && item.onClick) {
          console.log(`[GenericCardsStatistic] Calling item.onClick for card ${item.key}`);
          item.onClick(item, event);
        }
        // 然后执行 item.cardProps 的 onClick（如果存在）
        if (cardPropsOnClick) {
          console.log(`[GenericCardsStatistic] Calling cardProps.onClick for card ${item.key}`);
          cardPropsOnClick(event as any);
        }
      };
      
      const handleCardKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (isClickable) {
          handleKeyDown(event);
        }
        if (cardPropsOnKeyDown) {
          cardPropsOnKeyDown(event as any);
        }
      };
      
      const handleCardMouseEnter = (event: React.MouseEvent<HTMLDivElement>) => {
        handleMouseEnter(event);
        if (cardPropsOnMouseEnter) {
          cardPropsOnMouseEnter(event as any);
        }
      };
      
      return (
        <div
          className="statistic-card"
          style={mergedCardStyle}
          onClick={isClickable || cardPropsOnClick ? handleCardClick : undefined}
          onKeyDown={isClickable || cardPropsOnKeyDown ? handleCardKeyDown : undefined}
          onMouseEnter={handleCardMouseEnter}
          role={isClickable ? "button" : otherCardProps?.role}
          tabIndex={isClickable ? 0 : otherCardProps?.tabIndex}
          {...otherCardProps}
        >
          {statisticContent}
        </div>
      );
    }

    // 否则直接返回统计内容
    return statisticContent;
  };

  // 如果没有数据，返回空
  if (!data || data.length === 0) {
    return null;
  }

  return (
    <div
      className={`statistic-cards-container ${className}`}
      style={{
        display: "flex",
        flexWrap: "wrap",
        marginLeft: -gutter[0] / 2,
        marginRight: -gutter[0] / 2,
        ...style,
      }}
    >
      {data.map((item) => (
        <div
          key={item.key}
          className="statistic-card-wrapper"
          style={{
            width: `${100 / columns}%`,
            paddingLeft: gutter[0] / 2,
            paddingRight: gutter[0] / 2,
            marginBottom: gutter[1],
          }}
        >
          {renderStatisticCard(item)}
        </div>
      ))}
    </div>
  );
};

export default GenericCardsStatistic;
